import type { SafetyState, Telemetry } from '../protocol';
import type { CameraSpec, RobotProfile } from '../profiles/types';
import { LINK_PRESETS } from '../link/presets';
import type { SpawnPoint } from '../scene/course';
import { fmt, type SessionLog } from '../log/session';

export const STATE_LABELS: Record<SafetyState, string> = {
  READY: 'Готов',
  MOVING: 'Движение',
  DEGRADED_LINK: 'Связь деградирует',
  COMMAND_TIMEOUT: 'Нет команд — стоп',
  LINK_LOST: 'Связь потеряна — стоп',
  RECOVERING: 'Восстановление',
  EMERGENCY_STOP: 'АВАРИЙНЫЙ СТОП',
  FAULT: 'Неисправность',
};

export const STATE_LEVEL: Record<SafetyState, 'ok' | 'warn' | 'error'> = {
  READY: 'ok',
  MOVING: 'ok',
  DEGRADED_LINK: 'warn',
  COMMAND_TIMEOUT: 'warn',
  LINK_LOST: 'error',
  RECOVERING: 'warn',
  EMERGENCY_STOP: 'error',
  FAULT: 'error',
};

const STREAM_LABELS: Record<string, string> = {
  ok: 'поток ок',
  freeze: 'ФРИЗ',
  down: 'нет потока',
  recovering: 'восстановление',
  stale: 'кадр устарел',
};

export interface CamView {
  id: string;
  label: string;
  fps: number;
  kbit: number;
  dropPct: number;
  status: 'ok' | 'freeze' | 'down' | 'recovering' | 'stale';
  ageMs: number;
  primary: boolean;
  switching: boolean;
}

export interface RouteView {
  /** Сколько чекпоинтов пройдено */
  passed: number;
  total: number;
  /** Название следующего чекпоинта */
  next: string;
  timeMs: number;
  done: boolean;
}

export interface HudModel {
  now: number;
  presetName: string;
  rttMs: number;
  linkQuality: 'ok' | 'degraded' | 'lost';
  telemetry: Telemetry | null;
  telemetryAgeMs: number;
  state: SafetyState;
  cams: CamView[];
  cmd: { move: [number, number]; yaw: number; seq: number; rateHz: number; source: string };
  route: RouteView;
  renderFps: number;
  instructor: boolean;
}

export interface HudCallbacks {
  onPreset(id: string): void;
  onEstop(): void;
  onEstopRelease(): void;
  onRespawn(spawnId: string): void;
  onResetRoute(): void;
  onInstructor(on: boolean): void;
  onKeyboard(on: boolean): void;
  onDeadMan(on: boolean): void;
  onCutVideo(): void;
  onCutCommands(): void;
  onPrimaryCamera(id: string): void;
  onStick(x: number, y: number): void;
  onStickRelease(): void;
  onYaw(v: number): void;
  onYawRelease(): void;
  onDpad(v: number, w: number): void;
  onDpadRelease(): void;
}

/**
 * UI оператора — клон реального телеоп-интерфейса (п. 6 ТЗ): те же бейджи в
 * шапке, тот же порядок видеоокон (задняя слева, передняя справа), те же
 * названия органов управления. Оператору доступно только то, что есть на
 * боевом пульте: мини-карты, вида сверху и точной позиции нет.
 *
 * Отличия от боевого UI сделаны намеренно: возраст кадра и телеметрии
 * (учит различать живой ping и мёртвую картинку), явная индикация состояний
 * безопасности, блок маршрута и блок инструктора — последний в тренировочном
 * режиме закрывается.
 */
export class Hud {
  readonly videoCanvases = new Map<string, HTMLCanvasElement>();
  readonly instructorCanvas: HTMLCanvasElement;
  private badges = new Map<string, HTMLElement>();
  private camBoxes = new Map<
    string,
    { box: HTMLElement; stats: HTMLElement; age: HTMLElement; btn: HTMLButtonElement }
  >();
  private cmdReadout!: HTMLElement;
  private stateBanner!: HTMLElement;
  private diagList!: HTMLElement;
  private diagCount!: HTMLElement;
  private stickKnob!: HTMLElement;
  private yawSlider!: HTMLInputElement;
  private instructorWrap!: HTMLElement;
  private telemetryReadout!: HTMLElement;
  private routeLine!: HTMLElement;
  private routeSub!: HTMLElement;

  constructor(
    root: HTMLElement,
    private profile: RobotProfile,
    spawns: SpawnPoint[],
    private log: SessionLog,
    private cb: HudCallbacks,
  ) {
    root.innerHTML = '';
    root.append(this.buildHeader());

    const main = el('main', 'main');
    main.append(this.buildVideos(), this.buildPanel(spawns));
    root.append(main);

    this.instructorCanvas = el('canvas', 'instructor-canvas') as HTMLCanvasElement;
    this.instructorWrap = el('section', 'instructor hidden');
    this.instructorWrap.append(headline('Вид инструктора — оператору недоступен'), this.instructorCanvas);
    root.append(this.instructorWrap);
    root.append(this.buildDiagnostics());

    log.onDiag(() => this.renderDiagnostics());
    this.renderDiagnostics();
  }

  // ---------- построение ----------

  private buildHeader(): HTMLElement {
    const header = el('header', 'topbar');
    const brand = el('div', 'brand');
    brand.innerHTML = `<b>RoboSim</b><span>тренажёр телеуправления</span>`;
    header.append(brand);

    const badges = el('div', 'badges');
    for (const [key, label] of [
      ['link', 'Связь'],
      ['rtt', 'RTT'],
      ['battery', 'Заряд'],
      ['state', 'Режим'],
      ['cams', 'Камеры'],
      ['config', 'Config'],
    ] as const) {
      const b = el('div', 'badge');
      b.innerHTML = `<span class="badge-label">${label}</span><span class="badge-value">—</span>`;
      badges.append(b);
      this.badges.set(key, b);
    }
    header.append(badges);
    return header;
  }

  /** Порядок окон как на боевом пульте: задняя слева, передняя справа */
  private orderedCameras(): CameraSpec[] {
    return [...this.profile.cameras].sort((a, b) => (a.ui_slot ?? 9) - (b.ui_slot ?? 9));
  }

  private buildVideos(): HTMLElement {
    const section = el('section', 'videos');
    for (const cam of this.orderedCameras()) {
      const box = el('div', 'video');
      const canvas = el('canvas', 'video-canvas') as HTMLCanvasElement;
      canvas.width = 960;
      canvas.height = 540;
      this.videoCanvases.set(cam.id, canvas);

      const bar = el('div', 'video-bar');
      const btn = el('button', 'cam-btn') as HTMLButtonElement;
      btn.textContent = cam.label;
      btn.onclick = () => this.cb.onPrimaryCamera(cam.id);
      const stats = el('div', 'video-stats');
      stats.textContent = '—';
      bar.append(btn, stats);

      const age = el('div', 'video-age');
      age.textContent = 'кадр —';

      box.append(bar, canvas, age);
      section.append(box);
      this.camBoxes.set(cam.id, { box, stats, age, btn });
    }
    return section;
  }

  private buildPanel(spawns: SpawnPoint[]): HTMLElement {
    const panel = el('aside', 'panel');
    panel.append(this.buildControlBlock(), this.buildRouteBlock(), this.buildSafetyBlock());
    panel.append(this.buildLinkBlock(), this.buildInstructorBlock(spawns));
    return panel;
  }

  private buildControlBlock(): HTMLElement {
    const ctl = block('Управление роботом');

    // Переключение основной камеры
    const camRow = el('div', 'row');
    for (const cam of this.orderedCameras()) {
      const b = el('button', 'btn cam-select') as HTMLButtonElement;
      b.textContent = cam.short ?? cam.label;
      b.onclick = () => this.cb.onPrimaryCamera(cam.id);
      b.dataset.cam = cam.id;
      camRow.append(b);
    }
    ctl.append(camRow);

    // Команды движения. Боковой сдвиг доступен не всякой кинематике —
    // у дифференциального ровера его нет, и кнопки честно выключены.
    const canStrafe = this.profile.drive === 'legged';
    ctl.append(small('Команды движения'));
    const dpad = el('div', 'dpad');
    const keys: Array<[string, string, number, number, boolean, string]> = [
      ['up', '↑', 1, 0, true, 'Вперёд'],
      ['left', '‹', 0, 0, canStrafe, canStrafe ? 'Боком влево' : 'Боковой сдвиг недоступен этой кинематике'],
      ['stop', '■', 0, 0, true, 'Стоп'],
      ['right', '›', 0, 0, canStrafe, canStrafe ? 'Боком вправо' : 'Боковой сдвиг недоступен этой кинематике'],
      ['rotl', '↺', 0, -1, true, 'Разворот на месте влево'],
      ['down', '↓', -0.6, 0, true, 'Назад'],
      ['rotr', '↻', 0, 1, true, 'Разворот на месте вправо'],
    ];
    for (const [cls, glyph, v, w, enabled, title] of keys) {
      const b = el('button', `dpad-btn dpad-${cls}`) as HTMLButtonElement;
      b.textContent = glyph;
      b.title = title;
      b.disabled = !enabled;
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        if (b.disabled) return;
        b.setPointerCapture(e.pointerId);
        this.cb.onDpad(v, w);
      });
      b.addEventListener('pointerup', () => this.cb.onDpadRelease());
      b.addEventListener('pointercancel', () => this.cb.onDpadRelease());
      dpad.append(b);
    }
    ctl.append(dpad);

    // Джойстик и yaw
    ctl.append(small('Источник ввода и оси'), small2('move x, y'));
    const stickWrap = el('div', 'stick-wrap');
    const stick = el('div', 'stick');
    this.stickKnob = el('div', 'stick-knob');
    stick.append(el('div', 'stick-cross'), this.stickKnob);
    this.bindStick(stick);
    stickWrap.append(stick);
    ctl.append(stickWrap);

    ctl.append(small2('yaw'));
    this.yawSlider = el('input', 'yaw') as HTMLInputElement;
    this.yawSlider.type = 'range';
    this.yawSlider.min = '-1';
    this.yawSlider.max = '1';
    this.yawSlider.step = '0.02';
    this.yawSlider.value = '0';
    this.yawSlider.oninput = () => this.cb.onYaw(parseFloat(this.yawSlider.value));
    const yawRelease = (): void => {
      this.yawSlider.value = '0';
      this.cb.onYawRelease();
    };
    this.yawSlider.onpointerup = yawRelease;
    this.yawSlider.onpointercancel = yawRelease;
    this.yawSlider.onblur = yawRelease;
    ctl.append(this.yawSlider);

    this.cmdReadout = el('div', 'readout');
    ctl.append(this.cmdReadout);
    return ctl;
  }

  private buildRouteBlock(): HTMLElement {
    const b = block('Маршрут');
    this.routeLine = el('div', 'route-line');
    this.routeSub = el('div', 'hint');
    b.append(this.routeLine, this.routeSub, button('Сброс маршрута', () => this.cb.onResetRoute()));
    return b;
  }

  private buildSafetyBlock(): HTMLElement {
    const safety = block('Безопасность');
    this.stateBanner = el('div', 'state-banner');
    const estop = el('button', 'estop') as HTMLButtonElement;
    estop.textContent = 'АВАРИЙНЫЙ СТОП';
    estop.onclick = () => this.cb.onEstop();
    safety.append(
      this.stateBanner,
      estop,
      button('Снять аварийный стоп', () => this.cb.onEstopRelease()),
      toggle('Dead-man switch (ехать только при удержании)', this.profile.control.dead_man_switch, (on) =>
        this.cb.onDeadMan(on),
      ),
    );
    return safety;
  }

  private buildLinkBlock(): HTMLElement {
    const link = block('Связь');
    const select = el('select', 'select') as HTMLSelectElement;
    for (const p of LINK_PRESETS) {
      const o = document.createElement('option');
      o.value = p.id;
      o.textContent = p.name;
      select.append(o);
    }
    select.value = 'lte_good';
    const hint = el('div', 'hint');
    const setHint = (): void => {
      hint.textContent = LINK_PRESETS.find((p) => p.id === select.value)?.hint ?? '';
    };
    select.onchange = () => {
      setHint();
      this.cb.onPreset(select.value);
    };
    setHint();

    this.telemetryReadout = el('div', 'readout');
    link.append(
      select,
      hint,
      row(
        button('Обрыв видео 3 с', () => this.cb.onCutVideo()),
        button('Обрыв команд 2 с', () => this.cb.onCutCommands()),
      ),
      this.telemetryReadout,
    );
    return link;
  }

  private buildInstructorBlock(spawns: SpawnPoint[]): HTMLElement {
    const instr = block('Инструктор / разработчик');
    const spawnSel = el('select', 'select') as HTMLSelectElement;
    for (const s of spawns) {
      const o = document.createElement('option');
      o.value = s.id;
      o.textContent = s.name;
      spawnSel.append(o);
    }
    instr.append(
      toggle('Вид от третьего лица', false, (on) => this.cb.onInstructor(on)),
      toggle('Клавиатура (developer input)', true, (on) => this.cb.onKeyboard(on)),
      spawnSel,
      button('Телепорт к этапу', () => this.cb.onRespawn(spawnSel.value)),
    );
    return instr;
  }

  private buildDiagnostics(): HTMLElement {
    const sec = el('section', 'diagnostics');
    const head = el('div', 'diag-head');
    this.diagCount = el('span', 'diag-count');
    head.append(
      headline('Диагностика'),
      this.diagCount,
      linkButton('COPY', () => void this.log.copyDiagnostics()),
      linkButton('SAVE', () => this.log.download()),
    );
    this.diagList = el('div', 'diag-list');
    sec.append(head, this.diagList);
    return sec;
  }

  private bindStick(pad: HTMLElement): void {
    let active = false;
    const compute = (e: PointerEvent): void => {
      const r = pad.getBoundingClientRect();
      let x = ((e.clientX - r.left) / r.width) * 2 - 1;
      let y = -(((e.clientY - r.top) / r.height) * 2 - 1);
      const len = Math.hypot(x, y);
      if (len > 1) {
        x /= len;
        y /= len;
      }
      this.stickKnob.style.transform = `translate(${x * 42}%, ${-y * 42}%)`;
      this.cb.onStick(x, y);
    };
    pad.addEventListener('pointerdown', (e) => {
      active = true;
      pad.setPointerCapture(e.pointerId);
      compute(e);
    });
    pad.addEventListener('pointermove', (e) => {
      if (active) compute(e);
    });
    const release = (): void => {
      if (!active) return;
      active = false;
      this.stickKnob.style.transform = 'translate(0,0)';
      this.cb.onStickRelease();
    };
    pad.addEventListener('pointerup', release);
    pad.addEventListener('pointercancel', release);
    pad.addEventListener('pointerleave', release);
  }

  // ---------- обновление ----------

  update(m: HudModel): void {
    const linkText =
      m.linkQuality === 'ok' ? 'норма' : m.linkQuality === 'degraded' ? 'деградация' : 'нет связи';
    this.setBadge(
      'link',
      `${linkText} · ${m.presetName}`,
      m.linkQuality === 'ok' ? 'ok' : m.linkQuality === 'degraded' ? 'warn' : 'error',
    );
    this.setBadge('rtt', `${Math.round(m.rttMs)} мс`, m.rttMs < 200 ? 'ok' : m.rttMs < 450 ? 'warn' : 'error');

    const bat = m.telemetry?.battery_pct ?? 0;
    this.setBadge('battery', `${bat.toFixed(0)}%`, bat > 30 ? 'ok' : bat > 15 ? 'warn' : 'error');
    this.setBadge('state', STATE_LABELS[m.state], STATE_LEVEL[m.state]);

    const alive = m.cams.filter((c) => c.status !== 'down').length;
    this.setBadge('cams', `${alive}/${m.cams.length}`, alive === m.cams.length ? 'ok' : 'warn');

    const cal = this.profile.calibration.date;
    this.setBadge(
      'config',
      cal ? `${this.profile.id} · калибр. ${cal}` : `${this.profile.id} · без калибровки`,
      cal ? 'ok' : 'warn',
    );

    for (const c of m.cams) {
      const box = this.camBoxes.get(c.id);
      if (!box) continue;
      box.box.classList.toggle('primary', c.primary);
      box.btn.classList.toggle('active', c.primary);
      const age = c.ageMs === Infinity ? '—' : `${Math.round(c.ageMs)} мс`;
      box.age.textContent = `возраст кадра: ${age}`;
      box.age.className = `video-age ${c.ageMs > 700 ? 'error' : c.ageMs > 350 ? 'warn' : 'ok'}`;
      box.stats.textContent = c.switching
        ? 'переключение камеры…'
        : `${c.fps} fps · ${c.kbit} kbit/s · drop ${c.dropPct.toFixed(1)}% · ${STREAM_LABELS[c.status]}`;
      box.stats.classList.toggle('warn', c.status !== 'ok');
    }
    for (const b of document.querySelectorAll<HTMLButtonElement>('.cam-select')) {
      b.classList.toggle('active', m.cams.some((c) => c.id === b.dataset.cam && c.primary));
    }

    const tm = m.telemetry;
    this.cmdReadout.innerHTML =
      `<div><span>move y</span><b>${m.cmd.move[1].toFixed(2)}</b></div>` +
      `<div><span>yaw</span><b>${m.cmd.yaw.toFixed(2)}</b></div>` +
      `<div><span>seq</span><b>${m.cmd.seq}</b></div>` +
      `<div><span>частота</span><b>${m.cmd.rateHz} Гц</b></div>` +
      `<div><span>ввод</span><b>${m.cmd.source === 'training' ? 'экранный' : 'клавиатура'}</b></div>`;

    const ageLevel = m.telemetryAgeMs > 800 ? 'error' : m.telemetryAgeMs > 400 ? 'warn' : 'ok';
    this.telemetryReadout.innerHTML =
      `<div class="${ageLevel}"><span>возраст телеметрии</span><b>${
        Number.isFinite(m.telemetryAgeMs) ? Math.round(m.telemetryAgeMs) + ' мс' : '—'
      }</b></div>` +
      `<div><span>скорость</span><b>${(tm?.speed_ms ?? 0).toFixed(2)} м/с</b></div>` +
      `<div><span>покрытие</span><b>${tm?.surface ?? '—'}</b></div>` +
      `<div><span>уклон</span><b>${(tm?.slope_deg ?? 0).toFixed(1)}°</b></div>` +
      `<div><span>рендер</span><b>${m.renderFps} fps</b></div>`;

    this.stateBanner.textContent = STATE_LABELS[m.state];
    this.stateBanner.className = `state-banner ${STATE_LEVEL[m.state]}`;

    const r = m.route;
    this.routeLine.textContent = r.done
      ? `Маршрут пройден · ${fmt(r.timeMs)}`
      : `Чекпоинт ${r.passed} / ${r.total} · ${fmt(r.timeMs)}`;
    this.routeLine.className = `route-line ${r.done ? 'ok' : ''}`;
    this.routeSub.textContent = r.done ? 'Разбор — по логу сессии' : `Следующий: ${r.next}`;

    this.instructorWrap.classList.toggle('hidden', !m.instructor);
  }

  private setBadge(key: string, value: string, level: 'ok' | 'warn' | 'error'): void {
    const b = this.badges.get(key);
    if (!b) return;
    const v = b.querySelector('.badge-value')!;
    if (v.textContent !== value) v.textContent = value;
    b.className = `badge ${level}`;
  }

  private renderDiagnostics(): void {
    const items = this.log.diagnostics.slice(-120).reverse();
    this.diagList.innerHTML = items
      .map((d) => `<div class="diag-row ${d.level}"><span>${fmt(d.t)}</span>${escapeHtml(d.text)}</div>`)
      .join('');
    this.diagCount.textContent = `${this.log.size} записей`;
  }

  instructorSize(): [number, number] {
    const r = this.instructorCanvas.getBoundingClientRect();
    return [Math.max(320, r.width), Math.max(180, r.height)];
  }
}

// ---------- мелкие помощники ----------

function el(tag: string, cls = ''): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

function block(title: string): HTMLElement {
  const b = el('div', 'block');
  b.append(headline(title));
  return b;
}

function headline(text: string): HTMLElement {
  const h = el('h2', 'headline');
  h.textContent = text;
  return h;
}

function small(text: string): HTMLElement {
  const s = el('div', 'small');
  s.textContent = text;
  return s;
}

function small2(text: string): HTMLElement {
  const s = el('div', 'small dim');
  s.textContent = text;
  return s;
}

function row(...children: HTMLElement[]): HTMLElement {
  const r = el('div', 'row');
  r.append(...children);
  return r;
}

function button(text: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', 'btn') as HTMLButtonElement;
  b.textContent = text;
  b.onclick = onClick;
  return b;
}

function linkButton(text: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', 'link-btn') as HTMLButtonElement;
  b.textContent = text;
  b.onclick = onClick;
  return b;
}

function toggle(label: string, initial: boolean, onChange: (on: boolean) => void): HTMLElement {
  const wrap = el('label', 'toggle');
  const input = el('input') as HTMLInputElement;
  input.type = 'checkbox';
  input.checked = initial;
  input.onchange = () => onChange(input.checked);
  const span = el('span');
  span.textContent = label;
  wrap.append(input, span);
  return wrap;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]!);
}
